import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Event as EventIcon,
  Engineering as EngineeringIcon,
  Business as BusinessIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, addWeeks, isSameMonth, isSameDay, isToday, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import serviceRequestService from '../services/serviceRequest.service';
import commercialVisitService from '../services/commercialVisit.service';
import userService from '../services/user.service';

type ViewMode = 'day' | 'week' | 'month';
type EventType = 'all' | 'service_request' | 'commercial_visit';

interface CalendarEvent {
  id: string;
  title: string;
  type: 'service_request' | 'commercial_visit';
  date: Date;
  assignedTo?: any;
  status?: string;
  priority?: string;
  client?: any;
}

const Calendar: React.FC = () => {
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [eventType, setEventType] = useState<EventType>('all');
  const [selectedTechnician, setSelectedTechnician] = useState<string>('all');
  const [selectedSalesRep, setSelectedSalesRep] = useState<string>('all');
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [salesReps, setSalesReps] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Carregar usuários (técnicos e representantes)
  useEffect(() => {
    loadUsers();
  }, []);

  // Carregar eventos quando filtros mudarem
  useEffect(() => {
    loadEvents();
  }, [currentDate, viewMode, eventType, selectedTechnician, selectedSalesRep]);

  const loadUsers = async () => {
    try {
      const users = await userService.getUsers();
      console.log('Usuários carregados:', users);
      
      // Filtrar técnicos (role: technician ou field-agent)
      const techs = users.filter((u: any) => u.role === 'technician' || u.role === 'field-agent');
      console.log('Técnicos encontrados:', techs);
      
      // Filtrar representantes comerciais (role: commercial)
      const sales = users.filter((u: any) => u.role === 'commercial');
      console.log('Representantes comerciais encontrados:', sales);
      
      setTechnicians(techs);
      setSalesReps(sales);
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  const loadEvents = async () => {
    setLoading(true);
    try {
      const allEvents: CalendarEvent[] = [];

      // Carregar ordens de serviço
      if (eventType === 'all' || eventType === 'service_request') {
        const filters: any = {};
        if (selectedTechnician !== 'all') {
          filters.assignedTo = selectedTechnician;
        }
        
        const serviceRequests = await serviceRequestService.getServiceRequests(filters);
        const srEvents = serviceRequests.docs.map((sr: any) => ({
          id: sr._id,
          title: sr.title || `OS #${sr._id.slice(-6)}`,
          type: 'service_request' as const,
          date: parseISO(sr.scheduledDate || sr.createdAt),
          assignedTo: sr.assignedTo,
          status: sr.status,
          priority: sr.priority,
          client: sr.client
        }));
        allEvents.push(...srEvents);
      }

      // Carregar visitas comerciais
      if (eventType === 'all' || eventType === 'commercial_visit') {
        const filters: any = {};
        if (selectedSalesRep !== 'all') {
          filters.assignedTo = selectedSalesRep;
        }

        const visits = await commercialVisitService.getCommercialVisits(filters);
        const visitEvents = visits.docs.map((visit: any) => ({
          id: visit._id,
          title: visit.title || `Visita #${visit._id.slice(-6)}`,
          type: 'commercial_visit' as const,
          date: parseISO(visit.scheduledDate || visit.createdAt),
          assignedTo: visit.assignedTo,
          status: visit.status,
          client: visit.client
        }));
        allEvents.push(...visitEvents);
      }

      setEvents(allEvents);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, -1));
    } else {
      setCurrentDate(addMonths(currentDate, -1));
    }
  };

  const handleNext = () => {
    if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else {
      setCurrentDate(addMonths(currentDate, 1));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getEventsForDate = (date: Date) => {
    return events.filter(event => isSameDay(event.date, date));
  };

  const getEventColor = (event: CalendarEvent) => {
    if (event.type === 'service_request') {
      if (event.priority === 'high') return '#f44336';
      if (event.priority === 'medium') return '#ff9800';
      return '#2196f3';
    }
    return '#4caf50';
  };

  const getEventIcon = (event: CalendarEvent) => {
    return event.type === 'service_request' ? <EngineeringIcon /> : <BusinessIcon />;
  };

  const handleEventClick = (event: CalendarEvent) => {
    setSelectedEvent(event);
    setDetailsOpen(true);
  };

  const handleNavigateToEvent = () => {
    if (selectedEvent) {
      if (selectedEvent.type === 'service_request') {
        navigate(`/service-requests/${selectedEvent.id}`);
      } else {
        navigate(`/commercial-visits/${selectedEvent.id}`);
      }
    }
  };

  const renderDayView = () => {
    const dayEvents = getEventsForDate(currentDate);

    return (
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          {format(currentDate, "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
        </Typography>
        <Box sx={{ mt: 3 }}>
          {dayEvents.length > 0 ? (
            <Grid container spacing={2}>
              {dayEvents.map(event => (
                <Grid item xs={12} key={event.id}>
                  <Card 
                    sx={{ 
                      borderLeft: `4px solid ${getEventColor(event)}`,
                      cursor: 'pointer',
                      '&:hover': { boxShadow: 3 }
                    }}
                    onClick={() => handleEventClick(event)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {getEventIcon(event)}
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" fontWeight="bold">
                            {event.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {event.client?.name || 'Cliente não especificado'}
                          </Typography>
                        </Box>
                        {event.assignedTo && (
                          <Chip
                            avatar={<Avatar src={event.assignedTo.photoURL}>{event.assignedTo.displayName?.[0]}</Avatar>}
                            label={event.assignedTo.displayName}
                            size="small"
                          />
                        )}
                        <Chip
                          label={event.type === 'service_request' ? 'Ordem de Serviço' : 'Visita Comercial'}
                          size="small"
                          color={event.type === 'service_request' ? 'primary' : 'success'}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <EventIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                Nenhum evento agendado para este dia
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { locale: ptBR });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        <Grid container spacing={1}>
          {weekDays.map(day => {
            const dayEvents = getEventsForDate(day);
            const isCurrentDay = isToday(day);

            return (
              <Grid item xs={12 / 7} key={day.toString()}>
                <Paper
                  elevation={isCurrentDay ? 3 : 0}
                  sx={{
                    p: 1,
                    minHeight: 400,
                    bgcolor: isCurrentDay ? 'primary.light' : 'background.paper',
                    border: isCurrentDay ? '2px solid' : '1px solid',
                    borderColor: isCurrentDay ? 'primary.main' : 'divider'
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    align="center"
                    fontWeight="bold"
                    color={isCurrentDay ? 'primary.contrastText' : 'text.primary'}
                    gutterBottom
                  >
                    {format(day, 'EEE', { locale: ptBR })}
                  </Typography>
                  <Typography
                    variant="h6"
                    align="center"
                    color={isCurrentDay ? 'primary.contrastText' : 'text.primary'}
                    gutterBottom
                  >
                    {format(day, 'd')}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    {dayEvents.map(event => (
                      <Tooltip key={event.id} title={event.title}>
                        <Chip
                          size="small"
                          label={event.title.substring(0, 15) + (event.title.length > 15 ? '...' : '')}
                          onClick={() => handleEventClick(event)}
                          sx={{
                            mb: 0.5,
                            width: '100%',
                            bgcolor: getEventColor(event),
                            color: 'white',
                            '& .MuiChip-label': { fontSize: '0.7rem' }
                          }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Paper>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { locale: ptBR });
    const calendarEnd = endOfWeek(monthEnd, { locale: ptBR });

    const days = [];
    let day = calendarStart;
    while (day <= calendarEnd) {
      days.push(day);
      day = addDays(day, 1);
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    return (
      <Paper elevation={1} sx={{ p: 2 }}>
        {/* Cabeçalho dos dias da semana */}
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dayName => (
            <Grid item xs={12 / 7} key={dayName}>
              <Typography variant="subtitle2" align="center" fontWeight="bold" color="text.secondary">
                {dayName}
              </Typography>
            </Grid>
          ))}
        </Grid>

        {/* Dias do mês */}
        {weeks.map((week, weekIndex) => (
          <Grid container spacing={1} key={weekIndex} sx={{ mb: 1 }}>
            {week.map(day => {
              const dayEvents = getEventsForDate(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isCurrentDay = isToday(day);

              return (
                <Grid item xs={12 / 7} key={day.toString()}>
                  <Paper
                    elevation={isCurrentDay ? 3 : 0}
                    sx={{
                      p: 1,
                      minHeight: 100,
                      bgcolor: isCurrentDay ? 'primary.light' : isCurrentMonth ? 'background.paper' : 'action.hover',
                      opacity: isCurrentMonth ? 1 : 0.5,
                      border: isCurrentDay ? '2px solid' : '1px solid',
                      borderColor: isCurrentDay ? 'primary.main' : 'divider'
                    }}
                  >
                    <Typography
                      variant="body2"
                      align="center"
                      fontWeight={isCurrentDay ? 'bold' : 'normal'}
                      color={isCurrentDay ? 'primary.contrastText' : 'text.primary'}
                    >
                      {format(day, 'd')}
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {dayEvents.slice(0, 3).map(event => (
                        <Tooltip key={event.id} title={event.title}>
                          <Box
                            onClick={() => handleEventClick(event)}
                            sx={{
                              height: 4,
                              bgcolor: getEventColor(event),
                              borderRadius: 1,
                              mb: 0.5,
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.8 }
                            }}
                          />
                        </Tooltip>
                      ))}
                      {dayEvents.length > 3 && (
                        <Typography variant="caption" color="text.secondary" align="center" display="block">
                          +{dayEvents.length - 3}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        ))}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Calendário Geral
        </Typography>
      </Box>

      {/* Controles e Filtros */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Navegação de Data */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={handlePrevious} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <Button
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={handleToday}
                size="small"
              >
                Hoje
              </Button>
              <IconButton onClick={handleNext} size="small">
                <ChevronRightIcon />
              </IconButton>
              <Typography variant="h6" sx={{ ml: 2 }}>
                {viewMode === 'day' && format(currentDate, "d 'de' MMMM", { locale: ptBR })}
                {viewMode === 'week' && `Semana de ${format(startOfWeek(currentDate, { locale: ptBR }), "d 'de' MMM", { locale: ptBR })}`}
                {viewMode === 'month' && format(currentDate, "MMMM 'de' yyyy", { locale: ptBR })}
              </Typography>
            </Box>
          </Grid>

          {/* Modo de Visualização */}
          <Grid item xs={12} md={2}>
            <ButtonGroup size="small" fullWidth>
              <Button
                variant={viewMode === 'day' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('day')}
              >
                Dia
              </Button>
              <Button
                variant={viewMode === 'week' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('week')}
              >
                Semana
              </Button>
              <Button
                variant={viewMode === 'month' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('month')}
              >
                Mês
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Filtros */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={eventType}
                    label="Tipo"
                    onChange={(e) => setEventType(e.target.value as EventType)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="service_request">Ordens de Serviço</MenuItem>
                    <MenuItem value="commercial_visit">Visitas Comerciais</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Técnico</InputLabel>
                  <Select
                    value={selectedTechnician}
                    label="Técnico"
                    onChange={(e) => setSelectedTechnician(e.target.value)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {technicians.map(tech => (
                      <MenuItem key={tech._id} value={tech._id}>{tech.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Representante</InputLabel>
                  <Select
                    value={selectedSalesRep}
                    label="Representante"
                    onChange={(e) => setSelectedSalesRep(e.target.value)}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {salesReps.map(rep => (
                      <MenuItem key={rep._id} value={rep._id}>{rep.displayName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Conteúdo do Calendário */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {viewMode === 'day' && renderDayView()}
          {viewMode === 'week' && renderWeekView()}
          {viewMode === 'month' && renderMonthView()}
        </>
      )}

      {/* Dialog de Detalhes do Evento */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedEvent && getEventIcon(selectedEvent)}
            Detalhes do Evento
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEvent && (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedEvent.title}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Tipo:</strong> {selectedEvent.type === 'service_request' ? 'Ordem de Serviço' : 'Visita Comercial'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Data:</strong> {format(selectedEvent.date, "d 'de' MMMM 'de' yyyy", { locale: ptBR })}
                </Typography>
                {selectedEvent.client && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Cliente:</strong> {selectedEvent.client.name}
                  </Typography>
                )}
                {selectedEvent.assignedTo && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Responsável:</strong> {selectedEvent.assignedTo.displayName}
                  </Typography>
                )}
                {selectedEvent.status && (
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Status:</strong> {selectedEvent.status}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
          <Button variant="contained" onClick={handleNavigateToEvent}>
            Ver Detalhes Completos
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Calendar;
